import { useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { FinanceMovement, FinanceOverview, FinanceSummary } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { MovementForm } from './MovementForm';
import { FinanceDashboard } from './FinanceDashboard';
import { MaintenancePendingCard } from './MaintenancePendingCard';
import { PlanningCards } from './PlanningCards';
import { GivingCard } from './GivingCard';
import { PledgesTab } from './PledgesTab';
import { ACCOUNT_KIND, formatDay, formatMoney, monthStart } from './money';

type Tab = 'registrar' | 'movimientos' | 'reportes' | 'promesas' | 'ajustes';
const TABS: { key: Tab; label: string }[] = [
  { key: 'registrar', label: 'Registrar' },
  { key: 'movimientos', label: 'Movimientos' },
  { key: 'reportes', label: 'Reportes' },
  { key: 'promesas', label: 'Promesas' },
  { key: 'ajustes', label: 'Ajustes' },
];

const KIND_LABEL: Record<FinanceMovement['kind'], string> = { income: 'Ingreso', expense: 'Gasto', transfer_in: 'Transferencia recibida', transfer_out: 'Transferencia enviada' };

/** Finanzas (Motor 18): cuentas, libro de movimientos, resumen del mes y cierres. Todo acceso se audita. */
export function FinancePage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canManage = me.can('finance.manage');
  const [version, setVersion] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [params] = useSearchParams();
  const tabs = TABS.filter((t) => canManage || t.key !== 'registrar');
  const initial = params.get('ver');
  const [tab, setTab] = useState<Tab>(tabs.find((t) => t.key === initial)?.key ?? tabs[0]!.key);
  const overview = useAsync(
    (signal) => (churchId ? api.get<FinanceOverview>(`/finance/churches/${churchId}/accounts`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId, version],
  );
  // Al refrescar se conserva lo último cargado: la pantalla no parpadea ni pierde la pestaña elegida.
  const last = useRef<FinanceOverview | null>(null);
  if (overview.status === 'ready') last.current = overview.data;
  const data = overview.status === 'ready' ? overview.data : last.current;
  const refresh = (message?: string) => {
    setNotice(message ?? null);
    setVersion((v) => v + 1);
  };

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page">
      <header className="page__header">
        <h1>Finanzas</h1>
        <p className="muted">Cuentas, ingresos y gastos de la iglesia.</p>
        <Link className="btn btn--secondary" to="/app/finanzas/imagen">
          Imagen para proyectar
        </Link>
      </header>
      <Alert tone="info">Información sensible. Tu acceso queda registrado.</Alert>
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {overview.status === 'loading' && !data ? <Loading /> : null}
      {overview.status === 'error' ? <ErrorState error={overview.error} onRetry={overview.reload} /> : null}
      {data ? (
        <div className="tabs tabs--scroll" role="tablist" aria-label="Finanzas">
          {tabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`tabs__tab${tab === t.key ? ' tabs__tab--active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      ) : null}
      {data && tab === 'registrar' ? (
        <div className="stack">
          {data.accounts.some((a) => !a.archived) ? (
            <Card title="Registrar">
              <MovementForm churchId={churchId} overview={data} onSaved={refresh} />
            </Card>
          ) : (
            <EmptyState title="Primero crea una cuenta en Ajustes.">
              <Button variant="secondary" onClick={() => setTab('ajustes')}>
                Ir a Ajustes
              </Button>
            </EmptyState>
          )}
          <MaintenancePendingCard churchId={churchId} overview={data} onChange={refresh} />
        </div>
      ) : null}
      {data && tab === 'movimientos' ? <MovementsCard churchId={churchId} overview={data} canManage={canManage} version={version} onChange={refresh} /> : null}
      {data && tab === 'reportes' ? (
        <div className="stack">
          <FinanceDashboard churchId={churchId} overview={data} version={version} />
          <div className="columns">
            <SummaryCard churchId={churchId} today={data.today} version={version} />
            <GivingCard churchId={churchId} today={data.today} version={version} />
          </div>
        </div>
      ) : null}
      {data && tab === 'promesas' ? <PledgesTab churchId={churchId} overview={data} canManage={canManage} onChange={refresh} /> : null}
      {data && tab === 'ajustes' ? (
        <div className="columns">
          <div className="stack">
            <AccountsCard churchId={churchId} overview={data} canManage={canManage} onChange={refresh} />
            <ClosingCard churchId={churchId} overview={data} canManage={canManage} onChange={refresh} />
          </div>
          <div className="stack">
            <PlanningCards churchId={churchId} overview={data} canManage={canManage} onChange={refresh} />
            {canManage && me.can('church.manage') ? (
              <p className="muted small">
                Los cobros en línea con Mercado Pago ahora están en <Link to="/app/configuracion-iglesia#mercado-pago">Configuración de la iglesia</Link>.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AccountsCard({ churchId, overview, canManage, onChange }: { churchId: string; overview: FinanceOverview; canManage: boolean; onChange: (m?: string) => void }) {
  const [adding, setAdding] = useState(overview.accounts.length === 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/finance/churches/${churchId}/accounts`, { name: String(form.get('name')), kind: String(form.get('kind')) });
      setAdding(false);
      onChange('Cuenta creada.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function archive(id: string) {
    setError(null);
    try {
      await api.post(`/finance/churches/${churchId}/accounts/${id}/archive`);
      onChange('Cuenta archivada.');
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <Card
      title="Cuentas"
      action={
        canManage && !adding ? (
          <Button variant="ghost" onClick={() => setAdding(true)}>
            Nueva cuenta
          </Button>
        ) : undefined
      }
    >
      {overview.accounts.length === 0 ? <EmptyState title="Aún no hay cuentas." /> : null}
      <ul className="list">
        {overview.accounts.map((a) => (
          <li key={a.id} className="list__item list__item--actions movement">
            <span className="list__link">
              <strong>{a.name}</strong>
              <span className="muted small">{ACCOUNT_KIND[a.kind]}</span>
            </span>
            <strong className={a.balanceCents < 0 ? 'money money--negative' : 'money'}>{formatMoney(a.balanceCents, a.currency)}</strong>
            {canManage && a.balanceCents === 0 ? (
              <Button variant="ghost" onClick={() => archive(a.id)} aria-label={`Archivar ${a.name}`}>
                Archivar
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {error ? <Alert>{error}</Alert> : null}
      {adding && canManage ? (
        <form className="form" onSubmit={onSubmit} noValidate>
          <Field label="Nombre de la cuenta" name="name" required minLength={2} maxLength={80} placeholder="Caja general" />
          <div className="field">
            <label htmlFor="account-kind">Tipo</label>
            <select id="account-kind" name="kind" defaultValue="cash">
              {Object.entries(ACCOUNT_KIND).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Crear cuenta
            </Button>
            {overview.accounts.length > 0 ? (
              <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
                Cancelar
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function SummaryCard({ churchId, today, version }: { churchId: string; today: string; version: number }) {
  const from = monthStart(today);
  const summary = useAsync((signal) => api.get<FinanceSummary>(`/finance/churches/${churchId}/summary?from=${from}&to=${today}`, signal), [churchId, from, today, version]);
  // IMPLEMENTATION_DECISION_REQUIRED (IDR-022): una sola moneda por Iglesia; el resumen usa MXN.
  const money = (c: number) => formatMoney(c, 'MXN');
  return (
    <Card title="Este mes">
      {summary.status === 'loading' ? <Loading /> : null}
      {summary.status === 'error' ? <ErrorState error={summary.error} onRetry={summary.reload} /> : null}
      {summary.status === 'ready' ? (
        <>
          <div className="totals">
            <div>
              <span className="muted small">Ingresos</span>
              <strong className="money">{money(summary.data.incomeCents)}</strong>
            </div>
            <div>
              <span className="muted small">Gastos</span>
              <strong className="money">{money(summary.data.expenseCents)}</strong>
            </div>
            <div>
              <span className="muted small">Diferencia</span>
              <strong className={summary.data.incomeCents - summary.data.expenseCents < 0 ? 'money money--negative' : 'money'}>
                {money(summary.data.incomeCents - summary.data.expenseCents)}
              </strong>
            </div>
          </div>
          <ul className="list">
            {[...summary.data.income, ...summary.data.expense].map((r) => (
              <li key={r.category.id} className="list__item list__item--actions">
                <span>
                  {r.category.name} <span className="muted small">{r.category.kind === 'income' ? 'ingreso' : 'gasto'}</span>
                </span>
                <span className="money">{money(r.totalCents)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  );
}

type MovementFilter = { kind: '' | 'income' | 'expense'; accountId: string; categoryId: string; from: string; to: string };
const NO_FILTER: MovementFilter = { kind: '', accountId: '', categoryId: '', from: '', to: '' };

/** Movimientos con filtros arriba: tipo, cuenta y fechas los aplica el servidor; tipo y categoría, aquí. */
function MovementsCard({ churchId, overview, canManage, version, onChange }: { churchId: string; overview: FinanceOverview; canManage: boolean; version: number; onChange: (m?: string) => void }) {
  const [filter, setFilter] = useState<MovementFilter>(NO_FILTER);
  const query = new URLSearchParams(Object.entries({ accountId: filter.accountId, from: filter.from, to: filter.to }).filter(([, v]) => v)).toString();
  const list = useAsync(
    (signal) => api.get<{ items: FinanceMovement[] }>(`/finance/churches/${churchId}/movements${query ? `?${query}` : ''}`, signal).then((r) => r.items),
    [churchId, version, query],
  );
  const set = (patch: Partial<MovementFilter>) => setFilter((f) => ({ ...f, ...patch }));
  const categories = overview.categories.filter((c) => !filter.kind || c.kind === filter.kind);
  const shown =
    list.status === 'ready'
      ? list.data.filter((m) => {
          const kind = m.amountCents < 0 ? 'expense' : 'income';
          if (filter.kind && (m.kind === 'transfer_in' || m.kind === 'transfer_out' || kind !== filter.kind)) return false;
          return !filter.categoryId || m.category?.id === filter.categoryId;
        })
      : [];
  const filtered = Object.values(filter).some(Boolean);
  return (
    <Card title="Movimientos">
      <div className="form__row form__row--3" role="group" aria-label="Filtrar movimientos">
        <div className="field">
          <label htmlFor="mov-kind">Tipo</label>
          <select id="mov-kind" value={filter.kind} onChange={(e) => set({ kind: e.target.value as MovementFilter['kind'], categoryId: '' })}>
            <option value="">Todos</option>
            <option value="income">Ingresos</option>
            <option value="expense">Gastos</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="mov-account">Cuenta</label>
          <select id="mov-account" value={filter.accountId} onChange={(e) => set({ accountId: e.target.value })}>
            <option value="">Todas</option>
            {overview.accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="mov-category">Categoría</label>
          <select id="mov-category" value={filter.categoryId} onChange={(e) => set({ categoryId: e.target.value })}>
            <option value="">Todas</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form__row">
        <div className="field">
          <label htmlFor="mov-from">Desde</label>
          <input id="mov-from" type="date" value={filter.from} max={filter.to || undefined} onChange={(e) => set({ from: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="mov-to">Hasta</label>
          <input id="mov-to" type="date" value={filter.to} min={filter.from || undefined} onChange={(e) => set({ to: e.target.value })} />
        </div>
      </div>
      {filtered ? (
        <div className="actions">
          <Button variant="ghost" onClick={() => setFilter(NO_FILTER)}>
            Quitar filtros
          </Button>
        </div>
      ) : null}
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'ready' && shown.length === 0 ? <EmptyState title={filtered ? 'No hay movimientos con estos filtros.' : 'Aún no hay movimientos.'} /> : null}
      {list.status === 'ready' ? (
        <ul className="list">
          {shown.map((m) => (
            <MovementRow key={m.id} churchId={churchId} movement={m} canManage={canManage} onChange={onChange} />
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

function MovementRow({ churchId, movement: m, canManage, onChange }: { churchId: string; movement: FinanceMovement; canManage: boolean; onChange: (m?: string) => void }) {
  const [reversing, setReversing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onReverse(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/finance/churches/${churchId}/movements/${m.id}/reverse`, { reason: String(new FormData(event.currentTarget).get('reason')) });
      onChange('Movimiento revertido. El original queda en el historial.');
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  const title = m.reversalOf ? `Reversa: ${m.reason ?? ''}` : m.category?.name ?? KIND_LABEL[m.kind];
  return (
    <li className="list__item list__item--actions movement">
      <span className="list__link">
        <strong className={m.reversed ? 'struck' : undefined}>{title}</strong>
        <span className="muted small">
          {formatDay(m.occurredOn)} · {m.account.name}
          {m.person ? ` · ${m.person.name}` : ''}
          {m.description ? ` · ${m.description}` : ''}
          {m.reversed ? ' · Revertido' : ''}
        </span>
        <MovementFiles churchId={churchId} movement={m} canManage={canManage && m.kind !== 'transfer_in' && m.kind !== 'transfer_out'} onChange={onChange} />
      </span>
      <strong className={m.amountCents < 0 ? 'money money--negative' : 'money'}>{formatMoney(m.amountCents, m.account.currency)}</strong>
      {canManage && !m.reversed && !m.reversalOf && !reversing ? (
        <Button variant="ghost" onClick={() => setReversing(true)} aria-label={`Revertir ${title}`}>
          Revertir
        </Button>
      ) : null}
      {reversing ? (
        <form className="form movement__reverse" onSubmit={onReverse} noValidate>
          <Field label="Motivo de la reversa" name="reason" required minLength={3} maxLength={300} />
          {error ? <Alert>{error}</Alert> : null}
          <div className="actions">
            <Button type="submit" variant="danger" busy={busy}>
              Revertir
            </Button>
            <Button type="button" variant="ghost" onClick={() => setReversing(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  );
}

/** Comprobantes del movimiento: se abren en otra pestaña; quien administra adjunta o quita. */
function MovementFiles({ churchId, movement: m, canManage, onChange }: { churchId: string; movement: FinanceMovement; canManage: boolean; onChange: (m?: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const files = m.files ?? [];
  if (files.length === 0 && !canManage) return null;
  async function add(list: FileList | null) {
    if (!list?.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of list) await api.upload(`/finance/churches/${churchId}/movements/${m.id}/files`, f);
      onChange(list.length === 1 ? 'Comprobante adjuntado.' : `${list.length} comprobantes adjuntados.`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }
  async function remove(id: string, name: string) {
    if (!window.confirm(`¿Quitar el comprobante «${name}»?`)) return;
    try {
      await api.delete(`/finance/churches/${churchId}/files/${id}`);
      onChange('Comprobante quitado.');
    } catch (e) {
      setError(errorMessage(e));
    }
  }
  return (
    <span className="movement__files small">
      {files.map((f) => (
        <span key={f.id} className="movement__file">
          <a href={`/api/finance/churches/${churchId}/files/${f.id}`} target="_blank" rel="noreferrer">
            {f.mime === 'application/pdf' ? '📄' : '🖼️'} {f.name}
          </a>
          {canManage ? (
            <button type="button" className="link-button" aria-label={`Quitar ${f.name}`} onClick={() => void remove(f.id, f.name)}>
              ×
            </button>
          ) : null}
        </span>
      ))}
      {canManage ? (
        <>
          <input ref={input} type="file" multiple hidden accept="application/pdf,image/jpeg,image/png,image/webp,image/gif" onChange={(e) => void add(e.target.files)} aria-label="Adjuntar comprobante" />
          <button type="button" className="link-button" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? 'Subiendo…' : '📎 Adjuntar comprobante'}
          </button>
        </>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </span>
  );
}

function ClosingCard({ churchId, overview, canManage, onChange }: { churchId: string; overview: FinanceOverview; canManage: boolean; onChange: (m?: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const yesterday = (() => {
    const [y, m, d] = overview.today.split('-').map(Number);
    return new Date(Date.UTC(y!, m! - 1, d! - 1)).toISOString().slice(0, 10);
  })();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const throughDate = String(form.get('throughDate'));
    if (throughDate && !window.confirm(`¿Cerrar hasta el ${formatDay(throughDate)}? Ya no se podrá registrar ni corregir nada de esa fecha o anterior.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/finance/churches/${churchId}/closings`, { throughDate, note: String(form.get('note') || '') || null });
      onChange('Periodo cerrado.');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Cierre">
      <p className="muted small">
        {overview.closedThrough ? `Cerrado hasta el ${formatDay(overview.closedThrough)}. Nada puede registrarse con esa fecha o anterior.` : 'Aún no hay periodos cerrados.'}
      </p>
      {canManage ? (
        <form className="form" onSubmit={onSubmit} noValidate>
          <Field label="Cerrar hasta" name="throughDate" type="date" max={yesterday} min={overview.closedThrough ?? undefined} defaultValue={yesterday} required />
          <Field label="Nota (opcional)" name="note" maxLength={300} />
          {error ? <Alert>{error}</Alert> : null}
          <div className="actions">
            <Button type="submit" variant="secondary" busy={busy}>
              Cerrar periodo
            </Button>
          </div>
          <p className="muted small">Un cierre no se puede deshacer.</p>
        </form>
      ) : null}
    </Card>
  );
}
